import React, { useState, useRef, useEffect } from 'react';
import FormSelectBox from '../../../../../../components/Form/FormSelectBox';
import { useDocumentoTrabajo } from '../../../../../../hooks/useDocumentoTrabajo';
import { Button } from 'react-bootstrap';
import { uploadFile } from '../../../../../../services/Service';
import { useSolicitud } from '../../../../../../hooks/useSolicitud';
import MyModal from '../../../../../../components/Modal';
import { useDocumento } from '../../../../../../hooks/useDocumento';
import { toast } from 'react-toastify';
import { APROBADA_STATUS_ID } from '../../../../../../constants/constants';
import { logger } from '../../../../../../utils/logger';

type AgregarDocumentoProps = {
  tipoDocumentoDisabled?: boolean;
  documentoActividad: any[];
  cancelButtonFn?: () => void;
  saveCompletedButtonFn: (addDocument: DocumentoPersonal) => void;
  listDocumentos?: any[];
  isOtrosDocumentos?: boolean; // Nueva prop para identificar si es el modal de "Otros Documentos"
};

type DocumentoPersonal = {
  archivoSeleccionado: File | null;
  tipoDocumento: string;
  nombreArchivo: string;
  idDocumentoActividad: number;
  idTipoDocumento: number;
  sizeMaximo: number;
  fechaVencimiento?: string;
};

export const AgregarDocumento: React.FC<AgregarDocumentoProps> = ({
  tipoDocumentoDisabled,
  cancelButtonFn,
  saveCompletedButtonFn,
  documentoActividad,
  listDocumentos,
  isOtrosDocumentos = false,
}) => {
  const [existDocumentModal, setExistDocumentModal] = useState(false);
  const toggleExistDocument = () =>
    setExistDocumentModal(!existDocumentModal);
  const [vencimientoModal, setVencimientoModal] = useState(false);
  const toggleVencimiento = () =>
    setVencimientoModal(!vencimientoModal);
  const [notSaveDocumentModal, setNotSaveDocumentModal] =
    useState(false);
  const toggleNotSaveDocument = () =>
    setNotSaveDocumentModal(!notSaveDocumentModal);
  const [idDocumentoActividad, setIdDocumentoActividad] =
    useState<number>();
  const [fechaVencimiento, setFechaVencimiento] = useState<string>('');
  const today = new Date().toISOString().split('T')[0];
  const {
    documentosTrabajo,
    handleDocumentoTrabajoSelected,
    documentoTrabajoSelected,
    getFormatFile,
  } = useDocumentoTrabajo();
  const { solicitudSelected } = useSolicitud();
  const { handleLoading, buscarDocumentosByIdSolicitud } =
    useDocumento();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Editable gana: el documento es editable si alguna fila del catálogo con el mismo
  // tipo de documento es editable (no solo la fila puntual seleccionada)
  const fechaEditableSeleccionado =
    documentoActividad.some(
      (d) =>
        d.tipoDocumentoId === documentoTrabajoSelected?.tipoDocumentoId &&
        d.fechaEditable === true
    ) || documentoTrabajoSelected?.fechaEditable === true;

  // Estados para el select con filtro integrado (solo para "Otros Documentos")
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [filtroDocumento, setFiltroDocumento] = useState<string>('');
  const [documentosFiltrados, setDocumentosFiltrados] = useState<any[]>(documentoActividad);

  // useEffect para filtrar documentos cuando cambia el filtro o documentoActividad
  useEffect(() => {
    if (filtroDocumento.trim() !== '') {
      const filtrados = documentoActividad.filter((doc) =>
        doc.tipoDocumentoNombre?.toLowerCase().includes(filtroDocumento.toLowerCase())
      );
      setDocumentosFiltrados(filtrados);
    } else {
      setDocumentosFiltrados(documentoActividad);
    }
  }, [filtroDocumento, documentoActividad]);

  // useEffect para cerrar el dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.position-relative')) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Funciones para el select personalizado
  const handleSelectDocumento = (documento: any) => {
    setIdDocumentoActividad(documento.id);
    handleDocumentoTrabajoSelected(documento);
    setIsDropdownOpen(false);
    setFiltroDocumento(''); // Limpiar filtro al seleccionar
    setFechaVencimiento(''); // Reiniciar fecha al cambiar de tipo
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
    if (!isDropdownOpen) {
      setFiltroDocumento(''); // Limpiar filtro al abrir
    }
  };

  const getSelectedDocumentoName = () => {
    const selected = documentoActividad.find(doc => doc.id === idDocumentoActividad);
    return selected ? selected.tipoDocumentoNombre : 'Seleccionar';
  };

  const handleFileChange = () => {
    if (
      fileInputRef.current?.files &&
      fileInputRef.current.files[0]
    ) {
      const file = fileInputRef.current.files[0];
      setSelectedFile(file);
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Simula hacer clic en el input file
    }
  };

  const handleSaveButtonClick = () => {
    if (!idDocumentoActividad) {
      toast.error('Por favor, seleccione un tipo de documento.');
      return; // Detener la función si no se seleccionó un tipo de documento
    }

    // Validar que se haya cargado un documento válido
    if (!selectedFile) {
      toast.error('Por favor, cargue un documento.');
      return; // Detener la función si no se cargó un documento
    }

    // Si el tipo de documento tiene fecha de vencimiento editable, es obligatoria
    if (fechaEditableSeleccionado && !fechaVencimiento) {
      toast.error('Por favor, indique la Fecha de Vencimiento.');
      return;
    }
    logger.log('formatos', documentoTrabajoSelected?.formatos);
    logger.log(
      'getFormatFile(selectedFile.type)',
      getFormatFile(selectedFile.type)
    );
    if (
      !documentoTrabajoSelected.formatos.includes(
        getFormatFile(selectedFile.type)
      )
    ) {
      toast.error(
        `Por favor adjunte un archivo con el formato correcto: ${getAcceptFormat()}`
      );
      return;
    }

    const sizeMax = parseInt(
      documentoActividad.find((da) => da.id === idDocumentoActividad)
        .sizeMaximo
    );

    const toBytes: number = 1024 * 1024;
    const maxFileSize = toBytes * (sizeMax != null ? sizeMax : 20);

    if (selectedFile.size > maxFileSize) {
      toast.error(
        `El tamaño del archivo ${selectedFile.name} no puede superar los ${sizeMax} MB`
      );
      return;
    }

    btnRef!.current!.disabled = true;

    (async () => {
      try {
        const idTipoDocu = parseInt(
          documentoActividad.find(
            (da) => da.id === idDocumentoActividad
          ).tipoDocumentoId
        );
        const documentoExistente = listDocumentos?.find(
          (doc) => doc.idTipoDocumento === idTipoDocu
        );

        let puedeAdjuntarDoc = true;

        if (solicitudSelected.idTipoEstado === APROBADA_STATUS_ID) {
          // puedeAdjuntarDoc = await puedeAdjuntarDocumento(idTipoDocu, solicitudSelected.id);
        }

        if (!puedeAdjuntarDoc) {
          setVencimientoModal(true);
        } else {
          if (documentoExistente) {
            setExistDocumentModal(true);
          } else {
            saveCompletedButtonFn({
              archivoSeleccionado: selectedFile,
              idDocumentoActividad: idDocumentoActividad,
              nombreArchivo: selectedFile.name,
              idTipoDocumento: parseInt(
                documentoActividad.find(
                  (da) => da.id === idDocumentoActividad
                ).tipoDocumentoId
              ),
              tipoDocumento: documentoActividad.find(
                (da) => da.id === idDocumentoActividad
              ).tipoDocumentoNombre,
              sizeMaximo: parseInt(
                documentoActividad.find(
                  (da) => da.id === idDocumentoActividad
                ).sizeMaximo
              ),
              fechaVencimiento: fechaVencimiento || undefined,
            });
            cancelButtonFn?.();
          }
        }
      } catch (error) {
        logger.error(
          'Error al verificar la existencia del documento:',
          logger.safeError(error)
        );
        btnRef!.current!.disabled = false;
      }
    })();
  };

  const reloadDocumentoSolicitud = () => {
    handleLoading(true);
    buscarDocumentosByIdSolicitud(solicitudSelected.id);
  };

  const handleConfirmSaveFileClick = () => {
    btnRef!.current!.disabled = true;

    saveCompletedButtonFn({
      archivoSeleccionado: selectedFile,
      idDocumentoActividad: idDocumentoActividad!,
      nombreArchivo: selectedFile!.name,
      idTipoDocumento: parseInt(
        documentoActividad.find(
          (da) => da.id === idDocumentoActividad
        ).tipoDocumentoId
      ),
      tipoDocumento: documentoActividad.find(
        (da) => da.id === idDocumentoActividad
      ).tipoDocumentoNombre,
      sizeMaximo: parseInt(
        documentoActividad.find(
          (da) => da.id === idDocumentoActividad
        ).sizeMaximo
      ),
      fechaVencimiento: fechaVencimiento || undefined,
    });
    toggleExistDocument();
    cancelButtonFn?.();
  };

  const handleExitButtonClick = () => {
    if (selectedFile) {
      setNotSaveDocumentModal(true);
      return; // Detener la función si no se cargó un documento
    }

    cancelButtonFn?.();
  };

  const handleConfirmCloseModalesAll = () => {
    toggleNotSaveDocument();
    cancelButtonFn?.();
  };

  const getAcceptFormat = () => {
    logger.log('documentoTrabajoSelected id:', documentoTrabajoSelected?.id);

    if (
      !documentoTrabajoSelected ||
      !documentoTrabajoSelected.formatos ||
      !Array.isArray(documentoTrabajoSelected.formatos)
    ) {
      return '*/*'; // Permitir cualquier tipo de archivo si no se define correctamente el formato
    }
    const formatos = documentoTrabajoSelected.formatos.map(
      (formato) => {
        const lowerCaseFormat = formato.toLowerCase();
        return `.${lowerCaseFormat}`;
      }
    );

    return formatos.join(',');
  };

  return (
    <>
      <MyModal
        isOpen={notSaveDocumentModal}
        toggle={toggleNotSaveDocument}
        title="Advertencia"
        cancelButton={true}
        secondButton={true}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={handleConfirmCloseModalesAll}
      >
        <label>Desea Salir sin guardar los cambios?</label>
      </MyModal>
      <MyModal
        isOpen={existDocumentModal}
        toggle={toggleExistDocument}
        title="Advertencia"
        cancelButton={false}
        secondButton={true}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={handleConfirmSaveFileClick}
      >
        <label>Va a subir un tipo de documento ya anexado</label>
      </MyModal>
      <MyModal
        isOpen={vencimientoModal}
        toggle={toggleVencimiento}
        title="Advertencia"
        cancelButton={false}
        secondButton={true}
        secondButtonTitle={'Aceptar'}
        secondButtonFn={cancelButtonFn}
      >
        <label>
          Debe adjuntar un Tipo de Documento próximo a vencerse,
          vencido o no obligatorio
        </label>
      </MyModal>
      <div className="form-horizontal">
        <div className="row mt-2">
          {isOtrosDocumentos ? (
            // Select personalizado con filtro integrado tipo Select2
            <>
              <label className="col-form-label col-4 text-end">Tipo de Documento</label>
              <div className="col-8 position-relative">
                <div
                  className="form-control"
                  style={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundImage: 'none', // Remover flecha de Bootstrap
                    paddingRight: '2.5rem' // Espacio para nuestra flecha
                  }}
                  onClick={toggleDropdown}
                >
                  {getSelectedDocumentoName()}
                  <i
                    className={`fa fa-chevron-${isDropdownOpen ? 'up' : 'down'}`}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none'
                    }}
                  ></i>
                </div>

                {isDropdownOpen && (
                  <div
                    className="dropdown-menu show w-100 p-0"
                    style={{
                      maxHeight: '300px',
                      overflowY: 'auto',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      zIndex: 1050
                    }}
                  >
                    {/* Input de filtro dentro del dropdown */}
                    <div className="p-2 border-bottom">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Buscar tipo de documento..."
                        value={filtroDocumento}
                        onChange={(e) => setFiltroDocumento(e.target.value)}
                        autoFocus
                      />
                    </div>

                    {/* Lista de opciones filtradas */}
                    <div className="p-0">
                      {documentosFiltrados.length > 0 ? (
                        documentosFiltrados.map((documento) => (
                          <div
                            key={documento.id}
                            className="dropdown-item"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleSelectDocumento(documento)}
                          >
                            {documento.tipoDocumentoNombre}
                          </div>
                        ))
                      ) : (
                        <div className="dropdown-item-text text-muted">
                          No se encontraron resultados
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            // FormSelectBox normal para "Documentos Actividad"
            <FormSelectBox
              title={'Tipo de Documento'}
              label={'tipo de documento'}
              id={'tipoDocumento'}
              name={'idTipoDocumento'}
              optionId={'id'}
              optionLabel={'tipoDocumentoNombre'}
              value={idDocumentoActividad}
              options={documentoActividad}
              onChange={(
                event: React.ChangeEvent<HTMLSelectElement>
              ) => {
                // Obtener el valor seleccionado como cadena
                const selectedValue = event.target.value;
                // Convertir la cadena a número (si es válido)
                const parsedValue = parseInt(selectedValue, 10); // Base 10
                const documentoEncontrado = documentoActividad.find(
                  (dt) => dt.id === parsedValue
                );

                if (documentoEncontrado) {
                  // Si documentoEncontrado no es undefined, entonces puedes pasarlo como argumento
                  handleDocumentoTrabajoSelected(documentoEncontrado);
                }
                // Asignar el número a idTipoDocumento
                setIdDocumentoActividad(parsedValue);
                // Reiniciar la fecha de vencimiento al cambiar de tipo
                setFechaVencimiento('');
              }}
              //isDisabled={tipoDocumentoDisabled}
              classLabel={'col-form-label col-4 text-end'}
              classDivSelect="col-8"
            />
          )}
        </div>
        {fechaEditableSeleccionado &&
          !!idDocumentoActividad && (
            <div className="row mt-2">
              <label className="col-form-label col-4 text-end">
                Fecha de Vencimiento
              </label>
              <div className="col-8">
                <input
                  type="date"
                  className="form-control"
                  min={today}
                  value={fechaVencimiento}
                  onChange={(e) =>
                    setFechaVencimiento(e.target.value)
                  }
                />
              </div>
            </div>
          )}
        <div className="row mt-2">
          <div className="col-md-4 text-end">
            <label className={`col-form-label`}>Documento</label>
            <input
              type="file"
              accept={getAcceptFormat()}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              ref={fileInputRef}
            />
          </div>
          <div className="col-md-8">
            <div className="d-flex align-items-center justify-content-between">
              <Button
                className="btn-secondary pr-2"
                variant="secondary"
                onClick={handleButtonClick}
              >
                Examinar
              </Button>
              {selectedFile ? (
                <div>
                  <input
                    type="text"
                    style={{ width: '200px', height: '35px' }}
                    value={selectedFile.name}
                    readOnly // Esto hace que el campo sea no editable
                  />
                </div>
              ) : (
                <div>
                  <input
                    type="text"
                    style={{ width: '200px', height: '35px' }}
                    value="Ningún archivo seleccionado" // Texto por defecto
                    readOnly // Esto hace que el campo sea no editable
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-md-9"></div>{' '}
          {/* Espacio en blanco para empujar los botones hacia la derecha */}
          <div className="col-md-3 d-flex justify-content-end">
            {' '}
            {/* Coloca los botones a la derecha */}
            <Button
              color="secondary"
              onClick={handleSaveButtonClick}
              className="mr-2"
              ref={btnRef}
            >
              Guardar
            </Button>
            <Button
              color="secondary"
              onClick={handleExitButtonClick}
              className="mr-2"
            >
              Salir
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
